import { Button } from '@mui/material';
import React, { useCallback, useState } from 'react';
import { BeatLoader } from 'react-spinners';

import GoogleIcon from '../Icons/GoogleIcon';
import { getGoogleClientId, requestGoogleIdToken } from '../../utils/googleIdentity';
import { ssoButtonSx } from './ssoButtonStyles';

interface GoogleSignInButtonProps {
  onCredential: (googleIdToken: string) => void;
  onError: (message: string) => void;
  /** Called while the Google account picker / popup is open (mirrors MSAL inProgress). */
  onAccountPickerChange?: (open: boolean) => void;
  disabled?: boolean;
  loading?: boolean;
}

/**
 * Custom MUI Google button (matches Entra twin). Obtains a Google ID token, then
 * calls `onCredential` so existing validate-google / MFA logic stays unchanged.
 */
const GoogleSignInButton: React.FC<GoogleSignInButtonProps> = ({
  onCredential,
  onError,
  onAccountPickerChange,
  disabled = false,
  loading = false,
}) => {
  const clientId = getGoogleClientId();
  const [pickingAccount, setPickingAccount] = useState(false);

  const handleClick = useCallback(async () => {
    if (!clientId || pickingAccount || loading) return;

    setPickingAccount(true);
    onAccountPickerChange?.(true);
    try {
      const idToken = await requestGoogleIdToken(clientId);
      onCredential(idToken);
    } catch (error: unknown) {
      const message =
        error instanceof Error && error.message
          ? error.message
          : 'Cannot login with Google. Please contact the administrator.';
      onError(message);
    } finally {
      setPickingAccount(false);
      onAccountPickerChange?.(false);
    }
  }, [clientId, loading, onAccountPickerChange, onCredential, onError, pickingAccount]);

  if (!clientId) {
    return null;
  }

  const busy = disabled || pickingAccount || loading;

  return (
    <Button
      size="small"
      variant="outlined"
      color="inherit"
      type="button"
      className="full-width sso-button"
      sx={ssoButtonSx}
      onClick={handleClick}
      disabled={busy}
    >
      {pickingAccount ? (
        'Login is currently in progress'
      ) : loading ? (
        <>
          Verifying your credentials&nbsp;
          <BeatLoader size={8} color="#1b6bae" speedMultiplier={0.8} />
        </>
      ) : (
        <>
          <span className="sso-button__icon">
            <GoogleIcon />
          </span>
          Google
        </>
      )}
    </Button>
  );
};

export default GoogleSignInButton;
